import { useTranslation } from "react-i18next";
import { Button, Icon, Modal } from "semantic-ui-react";

import { DATE_FORMATS_FNS } from "components/DatePicker";
import { usePlanITAppointmentData } from "components/PlanIT/hooks";
import "components/PlanIT/PlanIT.scss";
import { PLAN_IT_BINARY_VALUES, PLAN_IT_DELIVER_TYPE } from "components/PlanIT/queryKeys";
import { formatTime, shouldRenderSection } from "components/PlanIT/utils";
import { formatDate } from "utils/dateHelperFunctions";

import type { PlanITDocumentationInfoType, PlanITReplacementVehicleInfo } from "components/PlanIT/queryKeys";

enum PLAN_IT_REPLACEMENT_VEHICLE_TYPE {
  Car = 0,
  Bike
}

enum PLAN_IT_COLLECT_TYPE {
  CarCompanyCollectsCustomer = 1,
  CarCompanyCollectsCar
}

type PlanITModalPropTypes = {
  closeModal: () => void;
  appointmentId: number;
};

export const PlanITModal = ({ closeModal, appointmentId }: PlanITModalPropTypes) => {
  const { t } = useTranslation();
  const { data } = usePlanITAppointmentData({ appointment_id: appointmentId });

  const renderData = (key: keyof PlanITDocumentationInfoType, text: string, value: string | number) => {
    if (data && !(key in data)) return;
    return (
      <>
        <span className="PlanITModal-FieldLabel">{text}</span>
        <span>{value}</span>
      </>
    );
  };

  const formatAddressInfo = ({ Street, HouseNumber, ZipCode, City, Remark }: PlanITReplacementVehicleInfo) => {
    return [`${Street ?? ""} ${HouseNumber ?? ""}`.trim(), ZipCode, City, Remark].filter(Boolean).join(", ");
  };

  return (
    <Modal open={true} className="PlanITModal">
      <Modal.Header>
        <span>{t("v8_plan_it_info", "Plan-IT Info")}</span>

        <Button className="modal-close-btn" basic onClick={closeModal}>
          <Icon className="xmark" />
        </Button>
      </Modal.Header>

      <Modal.Content>
        {data && (
          <div className="PlanITModal__content-wrapper">
            <div>
              {shouldRenderSection(["PlanDate", "PlanEndDate", "PlanTime", "PlanEndTime"], data) && (
                <>
                  <span className="PlanITModal__sectionLabel">{t("v8_date", "Date")}</span>
                  <div className="PlanITModal__gridContainer">
                    {renderData("PlanDate", t("v8_planned_date", "Planned Date"), data.PlanDate ? formatDate(data.PlanDate, DATE_FORMATS_FNS.dateMonthYear) : "")}
                    {renderData(
                      "PlanEndDate",
                      t("v8_planned_end_date", "Planned End Date"),
                      data.PlanEndDate ? formatDate(data.PlanEndDate, DATE_FORMATS_FNS.dateMonthYear) : ""
                    )}
                    {renderData("PlanTime", t("v8_planned_time", "Planned Time"), formatTime(data.PlanTime))}
                    {renderData("PlanEndTime", t("v8_planned_end_time", "Planned End Time"), formatTime(data.PlanEndTime))}
                  </div>

                  <div className="divider" />
                </>
              )}

              {shouldRenderSection(["Waits", "WaitsTime"], data) && (
                <>
                  <span className="PlanITModal__sectionLabel">{t("v8_wait", "Wait")}</span>
                  <div className="PlanITModal__gridContainer">
                    {renderData("Waits", t("v8_customer_waiting", "Customer Waiting"), data.Waits === PLAN_IT_BINARY_VALUES.Yes ? t("v8_yes", "Yes") : t("v8_no", "No"))}

                    {renderData("WaitsTime", t("v8_waiting_time", "Waiting time"), formatTime(data.WaitsTime))}
                  </div>
                  <div className="divider" />
                </>
              )}

              {shouldRenderSection(["Deadline", "DeadlineTime"], data) && (
                <>
                  <span className="PlanITModal__sectionLabel">{t("v8_deadline", "Deadline")}</span>
                  <div className="PlanITModal__gridContainer">
                    {renderData("Deadline", t("v8_date", "Date"), data.Deadline ? formatDate(data.Deadline, DATE_FORMATS_FNS.dateMonthYear) : "")}
                    {renderData("DeadlineTime", t("v8_deadline_time", "Deadline time"), formatTime(data.DeadlineTime))}
                  </div>
                  <div className="divider" />
                </>
              )}

              {shouldRenderSection(["SmsWhenReady"], data) && (
                <>
                  <span className="PlanITModal__sectionLabel">{t("v8_sms", "SMS")}</span>
                  <div className="PlanITModal__gridContainer">
                    {renderData(
                      "SmsWhenReady",
                      t("v8_send_sms_when_ready", "Send SMS When ready"),
                      data.SmsWhenReady === PLAN_IT_BINARY_VALUES.Yes ? t("v8_yes", "Yes") : t("v8_no", "No")
                    )}
                  </div>
                  <div className="divider" />
                </>
              )}

              {shouldRenderSection(["TimeAppointment", "DeliverDate", "DeliverTime", "TimeAppointmentCollectDate"], data) && (
                <>
                  <span className="PlanITModal__sectionLabel">{t("v8_time_appointment", "Time Appointment")}</span>
                  <div className="PlanITModal__gridContainer">
                    {renderData(
                      "TimeAppointment",
                      t("v8_time_appointment", "Time Appointment"),
                      data.TimeAppointment === PLAN_IT_BINARY_VALUES.Yes ? t("v8_yes", "Yes") : t("v8_no", "No")
                    )}

                    {renderData(
                      "DeliverDate",
                      t("v8_delivery_date", "Delivery date"),
                      data.TimeAppointmentDeliverDate ? formatDate(data.TimeAppointmentDeliverDate, DATE_FORMATS_FNS.dateMonthYear) : ""
                    )}
                    {renderData("DeliverTime", t("v8_delivery_time", "Delivery Time"), formatTime(data.TimeAppointmentDeliverTime))}
                    {renderData(
                      "TimeAppointmentCollectDate",
                      t("v8_collection_date", "Collection Date"),
                      data.TimeAppointmentCollectDate ? formatDate(data.TimeAppointmentCollectDate, DATE_FORMATS_FNS.dateMonthYear) : ""
                    )}

                    {renderData("TimeAppointmentCollectTime", t("v8_collection_time", "Collection Time"), formatTime(data.TimeAppointmentCollectTime))}
                  </div>
                  <div className="divider" />
                </>
              )}

              {shouldRenderSection(["Deliver", "DeliverType", "DeliverDate", "DeliverTime"], data) && (
                <>
                  <span className="PlanITModal__sectionLabel">{t("v8_delivery", "Delivery")}</span>
                  <div className="PlanITModal__gridContainer">
                    {renderData("Deliver", `${t("v8_delivery", "Delivery")}?`, data.Deliver === PLAN_IT_BINARY_VALUES.Yes ? t("v8_yes", "Yes") : t("v8_no", "No"))}

                    {renderData(
                      "DeliverType",
                      t("v8_delivery_type", "Delivery Type"),
                      data.DeliverType === PLAN_IT_DELIVER_TYPE.CarCompanyDeliversCar
                        ? t("v8_car_company_delivers_car", "Car Company Delivers Car")
                        : t("v8_car_company_delivers_customer", "Car Company Delivers Customer")
                    )}

                    {renderData(
                      "DeliverDate",
                      t("v8_delivery_date", "Delivery Date"),
                      data.DeliverDate ? formatDate(data.DeliverDate, DATE_FORMATS_FNS.dateMonthYear) : ""
                    )}
                    {renderData("DeliverTime", t("v8_delivery_time", "Delivery"), formatTime(data.DeliverTime))}
                  </div>
                  <div className="divider" />
                </>
              )}

              {shouldRenderSection(["Collect", "CollectType", "CollectDate", "CollectTime"], data) && (
                <>
                  <span className="PlanITModal__sectionLabel">{t("v8_collection", "Collection")}</span>
                  <div className="PlanITModal__gridContainer">
                    {renderData("Collect", t("v8_collection", "Collection"), data.Collect === PLAN_IT_BINARY_VALUES.Yes ? t("v8_yes", "Yes") : t("v8_no", "No"))}

                    {renderData(
                      "CollectType",
                      t("v8_collection_type", "Collection Type"),
                      data.CollectType === PLAN_IT_COLLECT_TYPE.CarCompanyCollectsCustomer
                        ? t("v8_car_company_collects_customer", "Car Company Collects Customer")
                        : t("v8_car_company_collects_car", "Car Company Collects Car")
                    )}

                    {renderData(
                      "CollectDate",
                      t("v8_collection_date", "Collection Date"),
                      data.CollectDate ? formatDate(data.CollectDate, DATE_FORMATS_FNS.dateMonthYear) : ""
                    )}
                    {renderData("CollectTime", t("v8_collection_time", "Collection Time"), formatTime(data.CollectTime))}
                  </div>
                  <div className="divider" />
                </>
              )}
            </div>

            <div>
              {shouldRenderSection(["Reception", "ReceptionPhone", "ReceptionEmail", "ReceptionFunction"], data) && (
                <>
                  <span className="PlanITModal__sectionLabel">{t("v8_reception", "Reception")}</span>
                  <div className="PlanITModal__gridContainer">
                    {renderData("Reception", t("v8_receptionist", "Receptionist"), data.Reception)}
                    {renderData("ReceptionPhone", t("v8_receptionist_phone", "Receptionist phone"), data.ReceptionPhone)}
                    {renderData("ReceptionEmail", t("v8_receptionist_email", "Receptionist Email"), data.ReceptionEmail)}
                    {renderData("ReceptionFunction", t("v8_receptionist_func", "Receptionist Function"), data.ReceptionFunction)}
                  </div>
                  <div className="divider" />
                </>
              )}

              {shouldRenderSection(["Mechanic", "Mechanic_ID"], data) && (
                <>
                  <span className="PlanITModal__sectionLabel">{t("v8_mechanic", "Mechanic")}</span>
                  <div className="PlanITModal__gridContainer">
                    {renderData("Mechanic", t("v8_mechanic", "Mechanic"), data.Mechanic)}
                    {renderData("Mechanic_ID", t("v8_mechanic_id", "Mechanic ID"), data.Mechanic_ID)}
                  </div>
                  <div className="divider" />
                </>
              )}

              {shouldRenderSection(
                [
                  "RentWise",
                  "ReplacementVehicle",
                  "ReplacementVehicleType",
                  "ReplacementVehicleBill",
                  "ReplacementVehicleRegistrationNumber",
                  "ReplacementVehiclePlanDate",
                  "ReplacementVehiclePlanEndDate",
                  "ReplacementVehiclePlanTime",
                  "ReplacementVehiclePlanEndTime",
                  "ReplacementVehicleCollectInfo",
                  "ReplacementVehicleDeliverInfo",
                  "PinBoard"
                ],
                data
              ) && (
                <>
                  <span className="PlanITModal__sectionLabel">{t("v8_rent", "Rent")}</span>
                  <div className="PlanITModal__gridContainer">
                    {renderData("RentWise", t("v8_rent_wise", "Rent Wise"), data.RentWise === PLAN_IT_BINARY_VALUES.Yes ? t("v8_yes", "Yes") : t("v8_no", "No"))}

                    {renderData(
                      "ReplacementVehicle",
                      t("v8_replacement_vehicle", "Replacement Vehicle"),
                      data.ReplacementVehicle === PLAN_IT_BINARY_VALUES.Yes ? t("v8_yes", "Yes") : t("v8_no", "No")
                    )}

                    {renderData(
                      "ReplacementVehicleType",
                      t("v8_replacement_vehicle_type", "Replacement Vehicle Type"),
                      data.ReplacementVehicleType === PLAN_IT_REPLACEMENT_VEHICLE_TYPE.Bike ? t("v8_bike", "Bike") : t("v8_car", "Car")
                    )}

                    {renderData("ReplacementVehicleBill", t("v8_replacement_vehicle_bill", "Replacement Vehicle Bill"), data.ReplacementVehicleBill)}
                    {renderData("ReplacementVehicleRegistrationNumber", `${t("v8_vehicle_reg", "Vehicle Registration")}#`, data.ReplacementVehicleRegistrationNumber)}

                    {renderData(
                      "ReplacementVehiclePlanDate",
                      t("v8_vehicle_planned_date", "Vehicle Planned Date"),
                      data.ReplacementVehiclePlanDate ? formatDate(data.ReplacementVehiclePlanDate, DATE_FORMATS_FNS.dateMonthYear) : ""
                    )}
                    {renderData(
                      "ReplacementVehiclePlanEndDate",
                      t("v8_vehicle_planned_end_date", "Vehicle Planned End Date"),
                      data.ReplacementVehiclePlanEndDate ? formatDate(data.ReplacementVehiclePlanEndDate, DATE_FORMATS_FNS.dateMonthYear) : ""
                    )}
                    {renderData("ReplacementVehiclePlanTime", t("v8_vehicle_planned_time", "Vehicle Planned Time"), formatTime(data.ReplacementVehiclePlanTime))}
                    {renderData(
                      "ReplacementVehiclePlanEndTime",
                      t("v8_vehicle_planned_end_time", "Vehicle Planned End Time"),
                      formatTime(data.ReplacementVehiclePlanEndTime)
                    )}
                    {renderData(
                      "ReplacementVehicleCollectInfo",
                      t("v8_replacement_vehicle_collection_info", "Replacement Vehicle Collection Info"),
                      formatAddressInfo(data.ReplacementVehicleCollectInfo)
                    )}
                    {renderData(
                      "ReplacementVehicleDeliverInfo",
                      t("v8_vehicle_delivery_info", "Vehicle Delivery Info"),
                      formatAddressInfo(data.ReplacementVehicleDeliverInfo)
                    )}

                    {renderData("PinBoard", t("v8_pinboard", "Pinboard"), data.PinBoard === PLAN_IT_BINARY_VALUES.Yes ? t("v8_yes", "Yes") : t("v8_no", "No"))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {!data && (
          <div className="PlanITModal-noData">
            <span>{t("v8_no_data_to_show", "No data to show")}</span>
          </div>
        )}
      </Modal.Content>
    </Modal>
  );
};
